import React, { useEffect, useState } from "react";
import { EyeOff, Eye } from "lucide-react";
import apiCall from "../network/api";
import { loader } from "../utils/svg";
import Button from "../utils/Button";
import { Modal } from "../utils/Modal";
import { getFormattedDateTime } from "../utils/genericFunctions";

export const ObcBankDetails = ({ email }) => {
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [updatedProfileData, setUpdatedProfileData] = useState({
    bankName: null,
    accNo: null,
    swiftCode: null,
  });
  const [errors, setErrors] = useState({
    bankName: "",
    bankAccount: "",
    swiftCode: "",
    password: "",
  });
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [inputsDisabled, setInputsDisabled] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [showPassport, setShowPassport] = useState(false);
  const [password, setPassword] = useState(null);
  const [bankDetailsExist, setBankDetailsExist] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [errorModal, setErrorModal] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getMaskedAccountNumber = (accNum) => {
    if (!accNum) return "";
    const sanitized = accNum.replace(/[^a-zA-Z0-9]/g, "");
    if (sanitized.length <= 4) return sanitized;
    const last4 = sanitized.slice(-4);
    return [..."X".repeat(sanitized.length - 4).match(/.{1,4}/g), last4].join(
      "-"
    );
  };

  const fetchData = async () => {
    try {
      const { data } = await apiCall({
        endpoint: "/otp/get-signup-row",
        method: "GET",
        params: { email },
      });
      const user = data;
      if (user) {
        setProfileData(user);
        setLoading(false);
        const exists = [
          user?.bankDetails?.accountNo,
          user?.bankDetails?.bankName,
          user?.bankDetails?.swiftCode,
        ].every(Boolean);
        setBankDetailsExist(exists);
        if (exists) setInputsDisabled(true);
      }
    } catch {
      setLoading(false);
    }
  };

  const validateBankName = (value) => {
    const trimmed = value.trim();
    return trimmed.length < 5
      ? "Minimum 5 characters needed"
      : /^[A-Za-zÀ-ÿ\u00C0-\u017F\s\-[\],.&']*$/.test(trimmed)
        ? ""
        : "Invalid characters in Bank Name";
  };

  const validateBankAccount = (value) => {
    const trimmed = value.trim().replace(/-/g, "");
    return trimmed.length > 35 ||
      trimmed.length < 5 ||
      !/^[A-Za-z0-9]*$/.test(trimmed)
      ? "Invalid Bank Account Number"
      : "";
  };

  const validateSwiftCode = (value) => {
    const trimmed = value.trim().toUpperCase();
    return trimmed.length > 15 ||
      trimmed.length < 8 ||
      !/^[A-Z0-9]*$/.test(trimmed)
      ? "Invalid Bank SWIFT Code"
      : "";
  };

  const verifyPassword = () => {
    if (password.toString() === profileData?.passportNo.toString()) {
      setInputsDisabled(false);
      setIsPasswordModalOpen(false);
      setPasswordError(false);
      setPassword(null);
    } else {
      setPasswordError(true);
    }
  };

  const handleSubmit = () => {
    const { bankName, accNo, swiftCode } = updatedProfileData;
    const newErrors = {
      bankName: bankName !== null ? validateBankName(bankName) : "",
      bankAccount: accNo !== null ? validateBankAccount(accNo) : "",
      swiftCode: swiftCode !== null ? validateSwiftCode(swiftCode) : "",
    };
    setErrors(newErrors);

    if (!newErrors.bankName && !newErrors.bankAccount && !newErrors.swiftCode) {
      handleRowUpdate({
        updateData: {
          bankDetails: {
            bankName: bankName ?? profileData?.bankDetails?.bankName,
            accountNo:
              accNo?.trim().replace(/-/g, "") ??
              profileData?.bankDetails?.accountNo,
            swiftCode: swiftCode ?? profileData?.bankDetails?.swiftCode,
          },
        },
      });
    }
  };

  const handleRowUpdate = async ({ updateData }) => {
    setIsSubmitLoading(true);
    const { bankName, accNo, swiftCode } = updatedProfileData;
    try {
      await apiCall({
        endpoint: "/otp/update-bank-details",
        method: "PUT",
        data: {
          emailId: email,
          updateData,
          emailData: {
            emailTo: email,
            emailBcc: "",
            status: bankDetailsExist ? "update" : "submit",
            dateTime: getFormattedDateTime(),
            bankDetails: {
              bank_name: bankName ?? profileData?.bankDetails?.bankName,
              bank_acc_number: getMaskedAccountNumber(
                (accNo ?? profileData?.bankDetails?.accountNo)
                  .trim()
                  .replace(/-/g, "")
              ),
              swift_code: swiftCode ?? profileData?.bankDetails?.swiftCode,
            },
          },
        },
      });
      setIsSubmitLoading(false);
      setIsSuccessModalOpen(true);
    } catch {
      setIsSubmitLoading(false);
      setErrorModal(true);
    }
  };

  const renderInput = (id, label, value, error, onChange, disabled) => (
    <div className="mb-3">
      <label
        htmlFor={id}
        className="block text-sm font-medium text-gray-500 mb-1"
      >
        {label}
      </label>
      <input
        type="text"
        id={id}
        name={id}
        value={value}
        className={`w-full px-3 py-2 rounded-md text-sm text-gray-500 font-medium ring-1 mb-1 ${
          error ? "ring-customError-300" : "ring-gray-150"
        } ${disabled ? "bg-gray-100" : ""}`}
        onChange={onChange}
        disabled={disabled}
      />
      {error && (
        <span className="text-customError-500 font-medium text-xs">
          {error}
        </span>
      )}
    </div>
  );

  return (
    <div className="max-w-md mx-auto bg-white p-6 rounded-lg shadow-sm">
      {loading ? (
        <div className="flex justify-center items-center">{loader()}</div>
      ) : (
        <>
          <h2 className="font-bold text-gray-500 mb-6">Bank Details</h2>
          <form className="">
            {renderInput(
              "bankName",
              "Bank Name",
              updatedProfileData?.bankName ??
                profileData?.bankDetails?.bankName,
              errors.bankName,
              (e) => {
                setUpdatedProfileData((p) => ({
                  ...p,
                  bankName: e.target.value,
                }));
                setErrors((e) => ({ ...e, bankName: "" }));
              },
              bankDetailsExist && inputsDisabled
            )}

            {renderInput(
              "accNo",
              "Bank Account Number",
              inputsDisabled
                ? getMaskedAccountNumber(profileData?.bankDetails?.accountNo)
                : (updatedProfileData?.accNo ??
                    profileData?.bankDetails?.accountNo),
              errors.bankAccount,
              (e) => {
                setUpdatedProfileData((p) => ({ ...p, accNo: e.target.value }));
                setErrors((e) => ({ ...e, bankAccount: "" }));
              },
              bankDetailsExist && inputsDisabled
            )}

            {renderInput(
              "swiftCode",
              "Bank SWIFT Code",
              updatedProfileData?.swiftCode ??
                profileData?.bankDetails?.swiftCode,
              errors.swiftCode,
              (e) => {
                setUpdatedProfileData((p) => ({
                  ...p,
                  swiftCode: e.target.value.toUpperCase(),
                }));
                setErrors((e) => ({ ...e, swiftCode: "" }));
              },
              bankDetailsExist && inputsDisabled
            )}
            <div className="w-full mt-6">
              <Button
                loading={isSubmitLoading}
                disabled={
                  !bankDetailsExist &&
                  ["bankName", "accNo", "swiftCode"].some(
                    (key) => !updatedProfileData[key]
                  )
                }
                onClick={() =>
                  inputsDisabled ? setIsPasswordModalOpen(true) : handleSubmit()
                }
                size="sm"
                text={bankDetailsExist ? "Update" : "Submit"}
              />
            </div>

            <Modal
              isOpen={isSuccessModalOpen}
              isSuccess
              successHeader="Bank Details Shared!"
              successMessage="Thank you, you should have also received an email confirmation of your details."
              successButton="Finish"
              successButtonAction={() => {
                fetchData();
                setIsSuccessModalOpen(false);
              }}
            />

            <Modal
              isOpen={errorModal}
              isFailure
              failureHeader="Sorry! Something Went Wrong!"
              failureMessage="We were unable to update your bank details. Please check the information and try again or reach out to obc@scienslogistics.com"
              failureButton="Try Again"
              failureButtonAction={() => {
                handleSubmit();
                setErrorModal(false);
              }}
              isCross
              crossAction={() => setErrorModal(false)}
            />

            <Modal
              isOpen={isPasswordModalOpen}
              header="Verify Password"
              message="Please enter your Password to verify"
              hasInput
              inputComponent={
                <div>
                  <div className="relative">
                    <input
                      type={showPassport ? "text" : "password"}
                      id="passport"
                      name="passport"
                      value={password || ""}
                      placeholder="Password"
                      className={`w-full px-3 py-2 rounded-md pr-10 text-sm text-gray-500 font-medium ring-1 ${
                        passwordError ? "ring-customError-300" : "ring-gray-150"
                      }`}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassport(!showPassport)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPassport ? (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {passwordError && (
                    <span className="text-customError-500 font-medium text-xs">
                      Password entered does not match, please check and try
                      again
                    </span>
                  )}
                </div>
              }
              isButtonDisabled={!password || password.length === 0}
              buttonTitle={passwordError ? "Try Again" : "Submit"}
              buttonAction={verifyPassword}
            />
          </form>
        </>
      )}
    </div>
  );
};
